import { ACADEMIC_STATUS_OPTIONS, ENROLLMENT_TYPE_OPTIONS } from './constants';
import { AcademicStatusCell } from './components/AcademicStatusCell';
import {
  BookOutlined,
  PieChartOutlined,
  SolutionOutlined,
  TagsOutlined
} from '@ant-design/icons';
import {
  CanAccess,
  CrudFilters,
  HttpError,
  useNotification
} from '@refinedev/core';
import { CorporateReportFilterForm } from './components/CorporateReportFilterForm';
import { CorporateReportShell } from './components/CorporateReportShell';
import { EnrollmentStatusCell } from './components/EnrollmentStatusCell';
import { ExportButton, TextField, useTable } from '@refinedev/antd';
import { Form, Grid, Input, Select, Table } from 'antd';
import { ICourseAcademicResult } from 'interfaces/corporate_reports';
import { ListLayout } from 'components/layout/layout/ListLayout';
import { ProgressPercentageCell } from './components/ProgressPercentageCell';
import { StudentInfoCell } from './components/StudentInfoCell';
import {
  buildCommonAcademicExportFilters,
  buildCommonAcademicSearchFilters,
  validateEnrollmentTypeSelection
} from './utils/filters';
import { useCallback, useEffect, useState } from 'react';
import { useCorporateCourseOptions } from './hooks/useCorporateCourseOptions';
import { useCorporateReportExport } from './hooks/useCorporateReportExport';

const REPORT_DESCRIPTION =
  'Este relatório permite à gestão corporativa acompanhar o rendimento dos alunos por curso,\noferecendo uma visão consolidada do desempenho acadêmico e do progresso na formação.';

const COLUMN_LEGEND_ITEMS = [
  {
    label: 'Versão',
    description:
      'Versão aplica-se apenas ao curso livre.\nV1 (depreciada): sem disciplinas.\nV2: com disciplinas.',
    icon: <TagsOutlined />,
    iconClassName: 'bg-amber-100 text-amber-700'
  },
  {
    label: 'Matrícula',
    description:
      '- Matrícula do aluno no curso.\n- Situação da matrícula do aluno no curso: Ativo ou Cancelado.',
    icon: <SolutionOutlined />,
    iconClassName: 'bg-violet-100 text-violet-700'
  },
  {
    label: 'Situação do curso',
    description:
      'Situaçao do aluno no curso: Em andamento, Aprovado ou Reprovado. Informa a etapa atual do aluno na formação.',
    icon: <BookOutlined />,
    iconClassName: 'bg-emerald-100 text-emerald-700'
  },
  {
    label: 'Progresso',
    description:
      'Percentual de conclusão do curso.\nCurso livre: conteúdos concluídos.\nPós-graduação: disciplinas aprovadas.',
    icon: <PieChartOutlined />,
    iconClassName: 'bg-sky-100 text-sky-700'
  }
];

interface IFilter {
  by_course_enrollment_type?: string;
  by_course_id?: string[];
  by_academic_status?: string;
  by_user_name?: string;
  by_user_email?: string;
}

const REPORT_STORAGE_KEY = 'course_academic_results_id';
const FREE_COURSE_ENROLLMENT_TYPE = 'Courses::Enrollment';
const COURSE_KIND_LABELS: Record<string, string> = {
  v1: 'V1 (depreciada)',
  v2: 'V2'
};
const getEnrollmentTypeLabel = (value?: string) =>
  ENROLLMENT_TYPE_OPTIONS.find(option => option.value === value)?.label ||
  value ||
  '-';
const getCourseKindLabel = (value?: string) =>
  COURSE_KIND_LABELS[value || ''] || value || '-';

export const CourseAcademicResults = () => {
  const [hasSearched, setHasSearched] = useState(false);
  const notification = useNotification();
  const screens = Grid.useBreakpoint();
  const isDesktop = !!screens.md;

  const { tableProps, searchFormProps } = useTable<
    ICourseAcademicResult,
    HttpError,
    IFilter
  >({
    resource: 'course_academic_results',
    queryOptions: { enabled: hasSearched },
    onSearch: params => buildCommonAcademicSearchFilters(params) as CrudFilters,
    sorters: {
      mode: 'server'
    }
  });
  const selectedEnrollmentType = Form.useWatch(
    'by_course_enrollment_type',
    searchFormProps.form
  );
  const showCourseKindColumn =
    selectedEnrollmentType === FREE_COURSE_ENROLLMENT_TYPE;

  const tablePropsWithConditionalData = hasSearched
    ? tableProps
    : { ...tableProps, dataSource: [], loading: false };
  const { courseOptions } = useCorporateCourseOptions(selectedEnrollmentType);

  useEffect(() => {
    searchFormProps.form?.setFieldValue('by_course_id');
  }, [searchFormProps.form, selectedEnrollmentType]);

  const { reportId, handleExportReport } = useCorporateReportExport<IFilter>({
    storageKey: REPORT_STORAGE_KEY,
    reportPath: '/admin/course_academic_results/report',
    searchFormProps,
    notification,
    buildExportFilters: formValues =>
      buildCommonAcademicExportFilters(formValues)
  });

  const validateFilter = useCallback(() => {
    return validateEnrollmentTypeSelection(
      searchFormProps.form?.getFieldValue('by_course_enrollment_type'),
      () =>
        notification.open?.({
          type: 'error',
          message: 'Modalidade obrigatória',
          description: 'Selecione a modalidade para aplicar o filtro.'
        })
    );
  }, [notification, searchFormProps.form]);

  return (
    <ListLayout
      title="Resultados Acadêmicos por Curso"
      headerActions={
        <CanAccess
          resource="corporate_reports/course_academic_results"
          action="list"
        >
          <ExportButton
            onClick={handleExportReport}
            disabled={!!reportId || !hasSearched || !selectedEnrollmentType}
            loading={!!reportId}
          >
            {!!reportId ? 'Relatório está sendo gerado' : 'Exportar'}
          </ExportButton>
        </CanAccess>
      }
    >
      <CorporateReportShell
        description={REPORT_DESCRIPTION}
        legendItems={COLUMN_LEGEND_ITEMS}
      >
        <CorporateReportFilterForm
          searchFormProps={searchFormProps}
          tableLoading={!!tableProps.loading}
          setHasSearched={setHasSearched}
          onValidateFilter={validateFilter}
        >
          <Form.Item className="mb-0 w-full sm:w-45" help={null}>
            <Form.Item
              name="by_course_enrollment_type"
              noStyle
              rules={[{ required: true, message: '' }]}
            >
              <Select
                allowClear
                placeholder="Modalidade"
                options={ENROLLMENT_TYPE_OPTIONS}
              />
            </Form.Item>
          </Form.Item>

          <Form.Item name="by_course_id" className="mb-0 w-full sm:w-45">
            <Select
              mode="multiple"
              allowClear
              disabled={!selectedEnrollmentType}
              style={{ width: '100%' }}
              placeholder="Curso"
              {...courseOptions}
            />
          </Form.Item>

          <Form.Item name="by_academic_status" className="mb-0 w-full sm:w-45">
            <Select
              allowClear
              placeholder="Situação do curso"
              options={ACADEMIC_STATUS_OPTIONS}
            />
          </Form.Item>

          <Form.Item name="by_user_name" className="mb-0 w-full sm:w-56">
            <Input placeholder="Nome" allowClear />
          </Form.Item>

          <Form.Item name="by_user_email" className="mb-0 w-full sm:w-56">
            <Input placeholder="E-mail" allowClear />
          </Form.Item>
        </CorporateReportFilterForm>

        <Table
          {...tablePropsWithConditionalData}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          pagination={{
            ...tablePropsWithConditionalData.pagination,
            showSizeChanger: isDesktop,
            simple: !isDesktop,
            showTotal: (total, range) =>
              `Mostrando ${range[0]} a ${range[1]} de ${total} resultados`
          }}
        >
          <Table.Column
            dataIndex="user_name"
            key="user_name"
            title="Informações do aluno"
            sorter
            fixed={isDesktop ? 'left' : undefined}
            className="min-w-[180px] max-w-[250px]"
            render={(_, record) => (
              <StudentInfoCell
                userName={record.user_name}
                userEmail={record.user_email}
              />
            )}
          />
          <Table.Column
            dataIndex="course_enrollment_type"
            key="course_enrollment_type"
            title="Modalidade"
            width={140}
            render={value => (
              <TextField value={getEnrollmentTypeLabel(value)} />
            )}
          />
          <Table.Column
            dataIndex="course_name"
            key="course_name"
            title="Curso"
            sorter
            className="min-w-[150px] max-w-[250px]"
            render={value => <TextField value={value} />}
          />
          {showCourseKindColumn && (
            <Table.Column
              dataIndex="course_kind"
              key="course_kind"
              title="Versão"
              width={140}
              render={value => <TextField value={getCourseKindLabel(value)} />}
            />
          )}
          <Table.Column
            dataIndex="enrollment_status"
            key="enrollment_status"
            title="Matrícula"
            width={180}
            render={(_, record) => (
              <EnrollmentStatusCell
                enrollmentRegistration={record.enrollment_registration}
                enrollmentStatus={record.enrollment_status}
              />
            )}
          />
          <Table.Column
            dataIndex="academic_status"
            key="academic_status"
            title="Situação do curso"
            width={190}
            render={value => <AcademicStatusCell status={value} />}
          />
          <Table.Column
            dataIndex="progress_percentage"
            key="progress_percentage"
            title="Progresso"
            fixed={isDesktop ? 'right' : undefined}
            width={90}
            align="center"
            sorter
            render={value => <ProgressPercentageCell value={value} />}
          />
        </Table>
      </CorporateReportShell>
    </ListLayout>
  );
};
